import { PhoneFieldFormatEnum, PhoneFieldFragmentFragment } from '../../../../__generated__/graphql';
import Input from '../../../../components/Input';
import { Paragraph } from '../../../../components/Text';
import { useGravityFormsDispatch, useSelectInitialized, useSelectPhoneFieldValue } from '../../state/hooks';
import ConditionalWrapper from '../ConditionalWrapper';
import FieldErrors from '../FieldErrors';
import FormFieldGridContainer from '../FormFieldGridContainer';

interface Props {
  field: PhoneFieldFragmentFragment;
}

interface PhoneFormatConfig {
  formatInstruction: string;
  maxLength: number;
  minLength: number;
}

const phoneFormatConfig: Record<PhoneFieldFormatEnum, PhoneFormatConfig> = {
  [PhoneFieldFormatEnum.International]: {
    formatInstruction: 'Enter a valid international phone number',
    maxLength: 17,
    minLength: 8,
  },
  [PhoneFieldFormatEnum.Standard]: {
    formatInstruction: 'Format must be (###) ###-####',
    maxLength: 14,
    minLength: 14,
  },
};

// Format phone number based on the phoneFormat
const formatPhoneNumber = (value: string, phoneFormat: PhoneFieldFormatEnum): string => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  switch (phoneFormat) {
    case PhoneFieldFormatEnum.International: {
      // International format: keep as entered with + prefix if needed
      if (digitsOnly.length === 0) {
        return '';
      }
      // Add + prefix if not present and value starts with digits
      if (!value.startsWith('+') && digitsOnly.length > 0) {
        return `+${digitsOnly}`;
      }

      return value.replace(/[^\d+]/g, ''); // Keep only digits and +
    }
    case PhoneFieldFormatEnum.Standard: {
      // US/Canada format: (###) ###-####
      if (digitsOnly.length === 0) {
        return '';
      }
      if (digitsOnly.length <= 3) {
        return `(${digitsOnly}`;
      }
      if (digitsOnly.length <= 6) {
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
      }

      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  }
};

const PhoneInput = ({ field }: Props) => {
  const {
    conditionalLogic,
    databaseId,
    description,
    isRequired,
    label,
    layoutGridColumnSpan,
    phoneFormat,
    placeholder,
  } = field;

  const dispatch = useGravityFormsDispatch();

  const fieldValue = useSelectPhoneFieldValue(databaseId);
  const initialized = useSelectInitialized();

  if (initialized && !fieldValue) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatPhoneNumber(inputValue, phoneFormat ?? PhoneFieldFormatEnum.Standard);

    dispatch({
      payload: {
        id: databaseId,
        value: formattedValue,
      },
      type: 'updatePhoneFieldValue',
    });
  };

  const {
    formatInstruction,
    maxLength,
    minLength,
  } = phoneFormatConfig[phoneFormat ?? PhoneFieldFormatEnum.Standard];

  return (
    <ConditionalWrapper conditionalLogic={conditionalLogic}>
      <FormFieldGridContainer layoutGridColumnSpan={layoutGridColumnSpan}>
        <Input
          autoComplete="tel"
          inputMode="tel"
          label={label ?? ''}
          maxLength={maxLength}
          minLength={minLength}
          name={databaseId.toString()}
          onChange={handleChange}
          placeholder={placeholder ?? ''}
          required={!!isRequired}
          type="tel"
          value={fieldValue?.value ?? ''}
        />

        {description && <Paragraph>{description}</Paragraph>}

        <FieldErrors
          fallback={formatInstruction}
          fieldId={databaseId}
        />
      </FormFieldGridContainer>
    </ConditionalWrapper>
  );
};

export default PhoneInput;
